import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import type { ActivationBrandingRequest } from '@/types/generated/activations.generated';

interface BrandingFormProps {
  value: ActivationBrandingRequest;
  onChange: (value: ActivationBrandingRequest) => void;
  idPrefix?: string;
}

const COLOR_FIELDS: { key: keyof ActivationBrandingRequest; label: string }[] = [
  { key: 'primaryColor', label: 'Primary colour' },
  { key: 'secondaryColor', label: 'Secondary colour' },
  { key: 'backgroundColor', label: 'Background colour' },
];

/** Shared branding editor for an event or an individual activation override. */
export function BrandingForm({ value, onChange, idPrefix = 'brand' }: BrandingFormProps) {
  const set = (key: keyof ActivationBrandingRequest, v: string) =>
    onChange({ ...value, [key]: v || null });

  return (
    <div className="space-y-4" data-testid="branding-form">
      <div className="grid gap-4 sm:grid-cols-3">
        {COLOR_FIELDS.map(({ key, label }) => (
          <div key={key} className="space-y-1.5">
            <Label htmlFor={`${idPrefix}-${key}`}>{label}</Label>
            <div className="flex items-center gap-2">
              <Input
                id={`${idPrefix}-${key}`}
                data-testid={`branding-${key}`}
                type="color"
                className="h-9 w-14 p-1"
                value={(value[key] as string) || '#000000'}
                onChange={(e) => set(key, e.target.value)}
              />
              <Input
                aria-label={`${label} hex`}
                value={(value[key] as string) ?? ''}
                placeholder="#000000"
                onChange={(e) => set(key, e.target.value)}
              />
            </div>
          </div>
        ))}
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-logoUrl`}>Logo URL</Label>
          <Input
            id={`${idPrefix}-logoUrl`}
            data-testid="branding-logoUrl"
            value={value.logoUrl ?? ''}
            placeholder="https://…"
            onChange={(e) => set('logoUrl', e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`${idPrefix}-fontFamily`}>Font family</Label>
          <Input
            id={`${idPrefix}-fontFamily`}
            data-testid="branding-fontFamily"
            value={value.fontFamily ?? ''}
            placeholder="Inter"
            onChange={(e) => set('fontFamily', e.target.value)}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Strips empty/null fields; returns undefined if the branding is entirely empty.
 * (The generated request contract types `branding` as optional and non-nullable, and
 * the platform omits nulls on write, so an absent value is the correct "no branding".)
 */
export function normalizeBranding(
  branding: ActivationBrandingRequest,
): ActivationBrandingRequest | undefined {
  const entries = Object.entries(branding).filter(([, v]) => v != null && v !== '');
  return entries.length > 0 ? (Object.fromEntries(entries) as ActivationBrandingRequest) : undefined;
}
